/**
 * Ordre d'affichage des cartes (liste de l'API, sélecteurs de l'admin et du
 * jeu) : le campus en haut (carte d'arrivée, puis les cartes dont le nom
 * commence par « Campus »), puis un groupe par bâtiment, ses niveaux dans
 * l'ordre (rez-de-chaussée, 1er étage, 2e…), puis les autres lieux.
 *
 * Le bâtiment se lit dans le nom affiché : « Copy Camp - 2e étage » → bâtiment
 * « Copy Camp », niveau « 2e étage ». Une variante entre parenthèses à la fin
 * (« (version Charles) ») fait un groupe à part, après celui sans variante.
 * Sous-chemin `@campus/core/zone-order` côté serveur, jamais le barrel.
 */

export interface OrderableZone {
  slug: string;
  displayName: string;
  isArrival?: boolean | undefined;
}

export interface ZoneGroup<T extends OrderableZone> {
  /** « Campus », nom du bâtiment (avec sa variante), ou « Autres lieux ». */
  title: string;
  kind: 'campus' | 'building' | 'other';
  zones: T[];
}

function fold(value: string): string {
  return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

const compare = (a: string, b: string) => a.localeCompare(b, 'fr', { sensitivity: 'base', numeric: true });

/** Bâtiment, niveau et variante lus dans le nom d'une carte. */
export function parseZoneName(displayName: string): { building: string | null; level: string; variant: string } {
  const variantMatch = /\s*(\([^()]*\))\s*$/.exec(displayName);
  const variant = variantMatch?.[1] ?? '';
  const base = variantMatch ? displayName.slice(0, variantMatch.index) : displayName;
  const sep = base.indexOf(' - ');
  if (sep < 0) return { building: null, level: base.trim(), variant };
  return { building: base.slice(0, sep).trim(), level: base.slice(sep + 3).trim(), variant };
}

/** Rang d'un niveau : rez-de-chaussée 0, « étage » ou 1er 1, 2e 2… ; inconnu à la fin. */
export function levelRank(level: string): number {
  const l = fold(level);
  if (/rez[- ]de[- ]chaussee|^rdc\b/.test(l)) return 0;
  const n = /(\d+)\s*(er|re|e|eme|ieme)?\b/.exec(l);
  if (n) return Number(n[1]);
  if (/sous[- ]sol/.test(l)) return -1;
  if (/^etage\b/.test(l)) return 1;
  return 1000;
}

const isCampus = (z: OrderableZone) => z.isArrival === true || fold(z.displayName).startsWith('campus');

/** Cartes regroupées dans l'ordre d'affichage. */
export function groupZones<T extends OrderableZone>(zones: T[]): ZoneGroup<T>[] {
  const campus = zones.filter(isCampus).sort((a, b) =>
    Number(b.isArrival === true) - Number(a.isArrival === true) || compare(a.displayName, b.displayName));

  const buildings = new Map<string, { building: string; variant: string; zones: T[] }>();
  const others: T[] = [];
  for (const z of zones) {
    if (isCampus(z)) continue;
    const { building, variant } = parseZoneName(z.displayName);
    if (!building) { others.push(z); continue; }
    const key = `${fold(building)}\u0000${fold(variant)}`;
    const group = buildings.get(key) ?? { building, variant, zones: [] };
    group.zones.push(z);
    buildings.set(key, group);
  }

  const byLevel = (a: T, b: T) => {
    const la = parseZoneName(a.displayName).level;
    const lb = parseZoneName(b.displayName).level;
    return levelRank(la) - levelRank(lb) || compare(la, lb);
  };
  const buildingGroups = [...buildings.values()]
    .sort((a, b) => compare(a.building, b.building) || Number(!!a.variant) - Number(!!b.variant) || compare(a.variant, b.variant))
    .map((g) => ({
      title: g.variant ? `${g.building} ${g.variant}` : g.building,
      kind: 'building' as const,
      zones: g.zones.sort(byLevel),
    }));

  return [
    ...(campus.length ? [{ title: 'Campus', kind: 'campus' as const, zones: campus }] : []),
    ...buildingGroups,
    ...(others.length ? [{ title: 'Autres lieux', kind: 'other' as const, zones: others.sort((a, b) => compare(a.displayName, b.displayName)) }] : []),
  ];
}

/** Cartes dans l'ordre d'affichage (même ordre que `groupZones`, à plat). */
export function orderZones<T extends OrderableZone>(zones: T[]): T[] {
  return groupZones(zones).flatMap((g) => g.zones);
}
